import { inject } from '@angular/core';
import { Routes, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RoutePermissions } from './core/auth/permission-catalog';
import { hasRequiredPermission } from './core/auth/auth-permissions.util';
import { authGuard, noAuthGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { roleGuard } from './core/guards/role.guard';
import { AuthActions, authFeature } from './features/auth/state';
import { AuthSessionSyncService } from './core/services/auth-session-sync.service';

const extraordinaryRequestsPermissionsGuard = () => {
  const store = inject(Store);
  const router = inject(Router);
  const sessionSync = inject(AuthSessionSyncService);
  const requiredPermissions = [
    RoutePermissions.gradeSheetsManage,
    RoutePermissions.paymentReferencesManage,
  ];
  const permissions = store.selectSignal(authFeature.selectPermissions);
  const roles = store.selectSignal(authFeature.selectRoles);
  const legacyRole = store.selectSignal(authFeature.selectRole);
  const session = sessionSync.readSession();

  if (!legacyRole() && !roles().length && session.token) {
    store.dispatch(AuthActions.initSuccess(session));
  }

  const activePermissions = permissions().length ? permissions() : session.permissions;
  if (
    requiredPermissions.every((permission) => hasRequiredPermission(activePermissions, permission))
  ) {
    return true;
  }

  return router.createUrlTree(['/access-denied'], {
    queryParams: { required: requiredPermissions.join(',') },
  });
};

const underConstructionComponent = () =>
  import('./features/under-construction/pages/under-construction-page/under-construction-page.component').then(
    (m) => m.UnderConstructionPageComponent,
  );

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./features/auth/components/pages/login-page/login-page.component').then(
        (m) => m.LoginPageComponent,
      ),
  },
  {
    path: 'reset-password',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./features/auth/components/pages/reset-password-page/reset-password-page.component').then(
        (m) => m.ResetPasswordPageComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./shared/components/layout/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    children: [
      {
        path: 'access-denied',
        loadComponent: () =>
          import('./features/access-denied/pages/access-denied-page/access-denied-page.component').then(
            (m) => m.AccessDeniedPageComponent,
          ),
      },
      /*
      {
        path: 'postgraduate/services/attendance',
        canActivate: [roleGuard],
        data: { roles: ['administrative', 'teacher'] },
        loadComponent: () =>
          import('./features/postgraduate/pages/attendance-page/attendance-page.component').then(
            (m) => m.AttendancePageComponent,
          ),
      },
      */
      {
        path: 'dashboard',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.dashboardView] },
        loadComponent: () =>
          import('./features/dashboard/components/pages/dashboard-page/dashboard-page.component').then(
            (m) => m.DashboardPageComponent,
          ),
      },
      {
        path: 'historial',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.academicEnrollmentsView],
        },
        loadComponent: () =>
          import('./features/academic-history/components/pages/academic-history-page/academic-history-page.component').then(
            (m) => m.AcademicHistoryPageComponent,
          ),
      },
      {
        path: 'payments',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'finance'],
          permissions: [RoutePermissions.financeViewPayments],
        },
        loadComponent: () =>
          import('./features/payments/pages/payments-page/payments-page.component').then(
            (m) => m.PaymentsPageComponent,
          ),
      },
      {
        path: 'finanzas/ingresos/historial-pagos',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'finance'],
          permissions: [RoutePermissions.financeViewPayments],
        },
        loadComponent: () =>
          import('./features/finance/pages/payment-history-page/payment-history-page.component').then(
            (m) => m.PaymentHistoryPageComponent,
          ),
      },
      {
        path: 'finanzas/ingresos/reportes',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'finance'],
          permissions: [RoutePermissions.financeViewReports],
        },
        loadComponent: () =>
          import('./features/finance/pages/income-reports-page/income-reports-page.component').then(
            (m) => m.IncomeReportsPageComponent,
          ),
      },
      {
        path: 'finanzas/ingresos/inconsistencias',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'finance'],
          permissions: [RoutePermissions.financeViewReports],
        },
        loadComponent: () =>
          import('./features/finance/pages/payment-inconsistencies-page/payment-inconsistencies-page.component').then(
            (m) => m.PaymentInconsistenciesPageComponent,
          ),
      },
      {
        path: 'finanzas/caja',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.paymentsManage],
        },
        loadComponent: () =>
          import('./features/finance/pages/cash-register-page/cash-register-page.component').then(
            (m) => m.CashRegisterPageComponent,
          ),
      },
      {
        path: 'finanzas/cobrar',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'finance'],
          permissions: [RoutePermissions.financeViewReports],
        },
        loadComponent: () =>
          import('./features/finance/pages/accounts-receivable-page/accounts-receivable-page.component').then(
            (m) => m.AccountsReceivablePageComponent,
          ),
      },
      {
        path: 'finanzas/conceptos-pago',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.paymentConceptsManage],
        },
        loadComponent: () =>
          import('./features/finance/pages/payment-concepts-page/payment-concepts-page.component').then(
            (m) => m.PaymentConceptsPageComponent,
          ),
      },
      {
        path: 'finanzas/payment-concept-groups',
        loadComponent: () =>
          import('./features/finance/pages/payment-concept-groups-page/payment-concept-groups-page.component').then(
            (m) => m.PaymentConceptGroupsPageComponent,
          ),
      },
      {
        path: 'finanzas/referencias-bancarias',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.paymentReferencesManage],
        },
        loadComponent: () =>
          import('./features/finance/pages/payment-references-page/payment-references-page.component').then(
            (m) => m.PaymentReferencesPageComponent,
          ),
      },
      {
        path: 'finanzas/planes-pago',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.paymentPlansManage],
        },
        loadComponent: () =>
          import('./features/finance/pages/payment-plans-page/payment-plans-page.component').then(
            (m) => m.PaymentPlansPageComponent,
          ),
      },
      {
        path: 'finanzas/facturacion',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Facturacion',
          description: 'La pantalla de facturación aún no está disponible.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'schedule',
        canActivate: [roleGuard],
        data: { roles: ['administrative'] },
        loadComponent: () =>
          import('./features/schedule/pages/schedule-page/schedule-page.component').then(
            (m) => m.SchedulePageComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/configuration/pages/user-profile-settings-page/user-profile-settings-page.component').then(
            (m) => m.UserProfileSettingsPageComponent,
          ),
      },
      {
        path: 'configuration/academic-calendar',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.academicCalendarManage],
        },
        loadComponent: () =>
          import('./features/configuration/pages/academic-calendar-page/academic-calendar-page.component').then(
            (m) => m.AcademicCalendarPageComponent,
          ),
      },
      {
        path: 'configuration/notifications',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
        },
        loadComponent: () =>
          import('./features/configuration/pages/notifications-page/notifications-page.component').then(
            (m) => m.NotificationsPageComponent,
          ),
      },
      {
        path: 'configuration/audit-logs',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.auditLogView],
        },
        loadComponent: () =>
          import('./features/configuration/pages/audit-logs-page/audit-logs-page.component').then(
            (m) => m.AuditLogsPageComponent,
          ),
      },
      {
        path: 'configuration/admission-settings',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
        },
        loadComponent: () =>
          import('./features/configuration/pages/admission-settings-page/admission-settings-page.component').then(
            (m) => m.AdmissionSettingsPageComponent,
          ),
      },
      {
        path: 'configuration/acciones',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Acciones formativas',
          description: 'La configuración de acciones formativas aún está en construcción.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'configuration/consultorias',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Consultorias',
          description: 'La configuración de consultorías aún está en construcción.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'configuration/catalogo',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Catálogo de oferta',
          description: 'La configuración del catálogo de oferta aún está en construcción.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'configuration/ventas',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Ventas y pedidos',
          description: 'La configuración de ventas y pedidos aún está en construcción.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'configuration/listas',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Listas para Moodle',
          description: 'La administración de listas para Moodle aún está en construcción.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'postgraduate/offer/study-plans',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.studyPlansManage],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/study-plans-page/study-plans-page.component').then(
            (m) => m.StudyPlansPageComponent,
          ),
      },
      {
        path: 'postgraduate/offer/subjects',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.subjectsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/subjects-page/subjects-page.component').then(
            (m) => m.SubjectsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/academic-periods',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.periodsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/academic-periods-page/academic-periods-page.component').then(
            (m) => m.AcademicPeriodsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/extraordinary-periods',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.gradeSheetsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/extraordinary-periods-page/extraordinary-periods-page.component').then(
            (m) => m.ExtraordinaryPeriodsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/extraordinary-requests',
        canActivate: [extraordinaryRequestsPermissionsGuard],
        data: {
          title: 'Solicitudes extraordinarias',
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/extraordinary-requests-page/extraordinary-requests-page.component').then(
            (m) => m.ExtraordinaryRequestsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/grade-corrections',
        canActivate: [permissionGuard],
        data: {
          permissions: [RoutePermissions.academicGradeCorrectionsManage],
          title: 'Correcciones de calificación',
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/grade-correction-requests-page/grade-correction-requests-page.component').then(
            (m) => m.GradeCorrectionRequestsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/extraordinary-exam-assignment',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.gradeSheetsManage],
          title: 'Asignación de examen extraordinario',
          description:
            'La asignación de exámenes extraordinarios estará disponible cuando la API publique su endpoint.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'postgraduate/services/admission',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.admissionManageForms, RoutePermissions.admissionExportCsv],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/admission-page/admission-page.component').then(
            (m) => m.AdmissionPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/transit-generation',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/transit-generation-page/transit-generation-page.component').then(
            (m) => m.TransitGenerationPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/applicants',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'admission'],
          permissions: [RoutePermissions.admissionManagePreregistration],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/applicants-page/applicants-page.component').then(
            (m) => m.ApplicantsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/pre-inscription',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.admissionManageForms],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/pre-inscription-page/pre-inscription-page.component').then(
            (m) => m.PreInscriptionPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/cohorts',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.groupCohortsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/cohorts-page/cohorts-page.component').then(
            (m) => m.CohortsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/cohorts/:cohortId/groups',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.groupsManage],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/cohort-groups-page/cohort-groups-page.component').then(
            (m) => m.CohortGroupsPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/group-management',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.groupsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/group-management-page/group-management-page.component').then(
            (m) => m.GroupManagementPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/groups/:id/details',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.enrollmentsManage, RoutePermissions.groupSchedulesManage],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/group-detail-page/group-detail-page.component').then(
            (m) => m.GroupDetailPageComponent,
          ),
      },
      {
        path: 'postgraduate/services/students',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative', 'teacher'],
          permissions: [RoutePermissions.studentsView],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/enrolled-students-page/enrolled-students-page.component').then(
            (m) => m.EnrolledStudentsPageComponent,
          ),
      },
      {
        path: 'postgraduate/academic-load/groups',
        canActivate: [roleGuard],
        data: { roles: ['teacher'] },
        loadComponent: () =>
          import('./features/postgraduate/pages/teacher-groups-page/teacher-groups-page.component').then(
            (m) => m.TeacherGroupsPageComponent,
          ),
      },
      {
        path: 'postgraduate/academic-load/grade-sheets',
        canActivate: [roleGuard],
        data: { roles: ['teacher'] },
        loadComponent: () =>
          import('./features/postgraduate/pages/teacher-grade-sheets-page/teacher-grade-sheets-page.component').then(
            (m) => m.TeacherGradeSheetsPageComponent,
          ),
      },
      {
        path: 'postgraduate/academic-load/schedules',
        canActivate: [roleGuard],
        data: { roles: ['teacher'] },
        loadComponent: () =>
          import('./features/postgraduate/pages/teacher-schedule-page/teacher-schedule-page.component').then(
            (m) => m.TeacherSchedulePageComponent,
          ),
      },
      {
        path: 'postgraduate/teachers/list',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.professorsManage] },
        loadComponent: () =>
          import('./features/postgraduate/pages/postgraduate-teachers-page/postgraduate-teachers-page.component').then(
            (m) => m.PostgraduateTeachersPageComponent,
          ),
      },
      {
        path: 'postgraduate/tracking/grade-sheets/extraordinary',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          title: 'Actas extraordinarias',
          description:
            'La generación de actas extraordinarias estará disponible cuando la API publique su endpoint.',
        },
        loadComponent: underConstructionComponent,
      },
      {
        path: 'postgraduate/tracking/grade-sheets',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/grade-sheets-page/grade-sheets-page.component').then(
            (m) => m.GradeSheetsPageComponent,
          ),
      },
      {
        path: 'postgraduate/tracking/kardex',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.academicEnrollmentsView],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/kardex-page/kardex-page.component').then(
            (m) => m.KardexPageComponent,
          ),
      },
      {
        path: 'postgraduate/tracking/academic-history',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.academicEnrollmentsView],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/academic-history-page/academic-history-page.component').then(
            (m) => m.AcademicHistoryPageComponent,
          ),
      },
      {
        path: 'postgraduate/tracking/graduation',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
        },
        loadComponent: () =>
          import('./features/postgraduate/pages/graduation-page/graduation-page.component').then(
            (m) => m.GraduationPageComponent,
          ),
      },
      {
        path: 'community/directory',
        canActivate: [roleGuard],
        data: { roles: ['administrative'] },
        loadComponent: () =>
          import('./features/community/pages/directory-page/directory-page.component').then(
            (m) => m.DirectoryPageComponent,
          ),
      },
      {
        path: 'community/access',
        canActivate: [roleGuard],
        data: { roles: ['administrative'] },
        loadComponent: () =>
          import('./features/community/pages/access-page/access-page.component').then(
            (m) => m.AccessPageComponent,
          ),
      },
      {
        path: 'constancias/listado',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.diplomasManage] },
        loadComponent: () =>
          import('./features/certificates/pages/certificate-list-page/certificate-list-page.component').then(
            (m) => m.CertificateListPageComponent,
          ),
      },
      {
        path: 'consulting/catalog',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.consultingManage],
        },
        loadComponent: () =>
          import('./features/consulting/pages/catalog-page/catalog-page.component').then(
            (m) => m.CatalogPageComponent,
          ),
      },
      {
        path: 'consulting/sales',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.consultingManage] },
        loadComponent: () =>
          import('./features/consulting/pages/sales-page/sales-page.component').then(
            (m) => m.SalesPageComponent,
          ),
      },
      {
        path: 'consulting/moodle',
        canActivate: [roleGuard],
        data: { roles: ['administrative'], permissions: [RoutePermissions.consultingManage] },
        loadComponent: () =>
          import('./features/consulting/pages/moodle-page/moodle-page.component').then(
            (m) => m.MoodlePageComponent,
          ),
      },
      {
        path: 'ed-continua/catalogo-oferta',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.extraActivitiesManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/catalog-offer-page/catalog-offer-page.component').then(
            (m) => m.CatalogOfferPageComponent,
          ),
      },
      {
        path: 'ed-continua/carga-matricula',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.bulkImportsManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/enrollment-upload-page/enrollment-upload-page.component').then(
            (m) => m.EnrollmentUploadPageComponent,
          ),
      },
      {
        path: 'ed-continua/gestion-modalidades',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.extraGroupsManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/gestion-modalidades/gestion-modalidades.component').then(
            (m) => m.GestionModalidadesComponent,
          ),
      },
      {
        path: 'ed-continua/administracion-grupos',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.extraGroupsManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/administracion-grupos/administracion-grupos.component').then(
            (m) => m.AdministracionGruposComponent,
          ),
      },
      {
        path: 'ed-continua/exportacion-grupos',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.moodleAccountsExport],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/exportacion-grupos/exportacion-grupos.component').then(
            (m) => m.ExportacionGruposComponent,
          ),
      },
      {
        path: 'ed-continua/acreditacion-grupo',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.evaluationsManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/acreditacion-grupo/acreditacion-grupo.component').then(
            (m) => m.AcreditacionGrupoComponent,
          ),
      },
      {
        path: 'ed-continua/gestion-casos',
        canActivate: [roleGuard],
        data: {
          roles: ['administrative'],
          permissions: [RoutePermissions.externalParticipantsManage],
        },
        loadComponent: () =>
          import('./features/continuing-education/pages/call-center/call-center.component').then(
            (m) => m.CallCenterComponent,
          ),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: '**',
        data: {
          title: 'Vista no disponible',
          description: 'Esta sección aún no tiene una pantalla operativa registrada.',
        },
        loadComponent: underConstructionComponent,
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
